import { AsyncPipe, DatePipe, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  HostListener,
  inject,
  Input,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { AuthorizationService } from '../../../../../../../cross-team-shared/misc-services/authorization.service';
import { isImageAnnotationOutput } from '../../../../../../../cross-team-shared/misc-utils/output-utils';
import { feedbackDialogService } from '../../../../../../../outputs/annotation/annotation-shared/annotation-providers';
import { getOutputById } from '../../../../../../h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { TaskWorkspaceStore } from '../../../../../../h3ml/state/task-workspace-store';
import { SamplingWorkspaceStore } from '../../../../../../sampling-workspace/state/sampling-workspace-store';
import { ClientFeedbackCrudService } from '../../../../../../shared/feedback/crud/client-feedback-crud.service';
import { ShapeFeedbackCrudService } from '../../../../../../shared/feedback/crud/shape-feedback-crud.service';
import { FeedbackListService } from '../../../../../../shared/feedback/feedback-list.service';
import { FusionManagerFactory } from '../../../fusion/fusion-manager-factory.service';
import { FeedbackDisplayItem } from '../feedback-list/feedback-list.component';

@Component({
  selector: 'h3-feedback-list-item',
  templateUrl: './feedback-list-item.component.html',
  styleUrls: [
    './feedback-list-item.component.scss',
    '../../../../../../shared/feedback/feedback.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIcon,
    DatePipe,
    AsyncPipe,
    MatMenu,
    NgIf,
    MatMenuTrigger,
    MatIconButton,
    MatMenuItem,
  ],
})
export class FeedbackListItemComponent extends DirectiveSuperclass {
  @Input() @HostBinding('class.is-selected') isSelected = false;
  @Input({ required: true }) feedback!: FeedbackDisplayItem;
  private readonly sampleStore = inject(SamplingWorkspaceStore);

  isMenuDisplayed$ = new BehaviorSubject(false);
  isForShapeFeedback = this.taskWorkspaceStore.state().feedbackType === 'shape';
  showFullComment = false;
  hasSampleFinalized$ = this.sampleStore.hasSampleFinalized$();
  footerText$: BehaviorSubject<string> = new BehaviorSubject('');

  private abstractFeedbackDialogService = inject(feedbackDialogService);
  private abstractFeedbackCrudService = this.isForShapeFeedback
    ? inject(ShapeFeedbackCrudService)
    : inject(ClientFeedbackCrudService);
  private fusionManager = inject(FusionManagerFactory).getManagers()[0];

  constructor(
    private feedbackListService: FeedbackListService,
    private taskWorkspaceStore: TaskWorkspaceStore,
    private authorizationService: AuthorizationService,
  ) {
    super();

    this.subscribeTo(this.getInput$('feedback'), () =>
      this.isMenuDisplayed$.next(this.shouldDisplayMenu()),
    );
    if (this.isForShapeFeedback) {
      this.subscribeTo(
        combineLatest([
          this.fusionManager.areAllSingleFrame$,
          this.getInput$('feedback'),
        ]),
        ([isSingleFrame, feedback]) =>
          this.footerText$.next(this.getFooterText(feedback, isSingleFrame)),
      );
    }
  }

  @HostListener('click')
  selectFeedback() {
    this.feedbackListService.setSelection(this.feedback.id);
  }

  @HostListener('dblclick')
  openClientViewDetail() {
    this.abstractFeedbackDialogService.openDetailView(
      this.feedback.coordinates,
    );
  }

  editFeedback() {
    this.feedbackListService.setSelection(this.feedback.id);
    this.abstractFeedbackDialogService.openEditFeedbackDialog();
  }

  deleteFeedback(
    feedback: FeedbackDisplayItem,
    event: MouseEvent,
    trigger: MatMenuTrigger,
  ) {
    // for some reason, specifically for delete if the feedback item is inside
    // another mat menu, it also closes the parent mat menu, stopping
    // propagation and manually closing this menu fixes it
    event.stopPropagation();
    this.feedbackListService.setSelection(this.feedback.id);
    this.abstractFeedbackCrudService.delete(feedback.id, feedback.shapeId);
    trigger.closeMenu();
  }

  toggleShowFullComment() {
    this.showFullComment = !this.showFullComment;
  }

  private shouldDisplayMenu() {
    const workspace = this.taskWorkspaceStore.state().workspace;
    return workspace === 'sampling' || this.shouldDisplayMenuInReadonly();
  }

  private shouldDisplayMenuInReadonly() {
    return (
      this.taskWorkspaceStore.state().workspace === 'readOnly' &&
      !this.isForShapeFeedback &&
      this.authorizationService.hasPermission('issue_create') &&
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      !this.feedback?.isClosed
    );
  }

  private getFooterText(feedback: FeedbackDisplayItem, isSingleFrame: boolean) {
    const shapeIdText = isDefined(feedback.shapeId)
      ? `Shape #${feedback.shapeId}`
      : '';
    if (isSingleFrame) {
      return shapeIdText;
    }
    let frameText = '';
    if (
      !isImageAnnotationOutput(
        getOutputById(
          this.taskWorkspaceStore.state().project,
          feedback.outputId,
        ),
      )
    ) {
      if (feedback.startFrame === feedback.endFrame) {
        frameText = `Frame ${feedback.startFrame}`;
      } else {
        frameText = `Frame ${feedback.startFrame} to ${feedback.endFrame}`;
      }
    }
    return [shapeIdText, frameText].filter((x) => x !== '').join(' - ');
  }
}
